
import React, { createContext, useContext, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNotification } from '../../hooks/useNotif';

const NotificationContext = createContext();

export const useNotificationContext = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotificationContext must be used within NotifWrapper');
    }
    return context;
};

const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

export const NotifWrapper = ({
    children,
    autoRequest = false,
    enableBackgroundQueue = true,
    onPermissionGranted,
    onPermissionDenied
}) => {
    const notification = useNotification();
    const notificationQueue = useRef([]);
    const isProcessing = useRef(false);

    const isPushSupported = useMemo(() =>
        'serviceWorker' in navigator && 'PushManager' in window,
        []);

    const getServiceWorker = useCallback(async () => {
        if (!isPushSupported) return null;

        try {
            const registration = await navigator.serviceWorker.ready;
            return registration;
        } catch (error) {
            console.error('❌ Ошибка получения Service Worker:', error);
            return null;
        }
    }, [isPushSupported]);

    const subscribeToPush = useCallback(async () => {
        if (!isPushSupported) {
            console.log('ℹ️ Push не поддерживается браузером');
            return null;
        }

        try {
            const registration = await getServiceWorker();
            if (!registration) return null;

            const vapidPublicKey = "BAfb_HnTmARvuO8aNhbFb7z3EjL_0u8NmXym3j4PkTxRLQ0Q-YPMQx1j3iSmh6fXxpof7A2vzkr4Y_rt3P8M20A";

            const subscribeOptions = {
                userVisibleOnly: true, 
            };

            if (vapidPublicKey) {
                subscribeOptions.applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
            }

            const subscription = await registration.pushManager.subscribe(subscribeOptions);
            console.log('✅ Подписка на Push выполнена:', subscription);

            return subscription; 
        } catch (error) {
            console.error('❌ Ошибка подписки на Push:', error);
            return null;
        }
    }, [isPushSupported, getServiceWorker]);

    const processQueue = useCallback(() => {
        if (isProcessing.current || notificationQueue.current.length === 0) return;

        isProcessing.current = true;
        const queueItem = notificationQueue.current.shift();

        if (queueItem) {
            const { title, options, resolve, reject } = queueItem;

            try {
                const notif = notification.showNotification(title, options);
                resolve(notif);
            } catch (error) {
                reject(error);
            } finally {
                setTimeout(() => {
                    isProcessing.current = false;
                    processQueue();
                }, 1000);
            }
        } else {
            isProcessing.current = false;
        }
    }, [notification]);

    const queueNotification = useCallback((title, options = {}) => {
        return new Promise((resolve, reject) => {
            notificationQueue.current.push({
                title,
                options,
                resolve,
                reject
            });
            processQueue();
        });
    }, [processQueue]);

    const sendBackgroundNotification = useCallback(async (title, options = {}) => {
        if (!isPushSupported) {
            return queueNotification(title, options);
        }

        try {
            const registration = await getServiceWorker();
            if (registration && registration.active) {
                registration.active.postMessage({
                    type: 'SHOW_NOTIFICATION',
                    title,
                    options: {
                        icon: '/favicon.ico',
                        badge: '/favicon.ico',
                        ...options
                    }
                });
                return true;
            }
        } catch (error) {
            console.error('❌ Ошибка отправки фонового уведомления:', error);
            return queueNotification(title, options);
        }
    }, [isPushSupported, getServiceWorker, queueNotification]);

    useEffect(() => {
        if (autoRequest && notification.permission === 'granted' && isPushSupported) {
            subscribeToPush();
        }
    }, [autoRequest, notification.permission, isPushSupported, subscribeToPush]);

    const value = useMemo(() => ({
        ...notification,
        isPushSupported,
        subscribeToPush,
        queueNotification,
        sendBackgroundNotification,

        showSuccess: (message, options = {}) =>
            queueNotification('Успех', {
                body: message,
                icon: '/icons/success.png',
                tag: 'success',
                ...options
            }),

        showError: (message, options = {}) =>
            queueNotification('Ошибка', {
                body: message,
                icon: '/icons/error.png',
                tag: 'error',
                requireInteraction: true,
                ...options
            }),

        showInfo: (message, options = {}) =>
            queueNotification('Информация', {
                body: message,
                icon: '/icons/info.png',
                tag: 'info',
                ...options
            }),

        showBatch: (notifications) => {
            return Promise.all(
                notifications.map(notif =>
                    queueNotification(notif.title, notif.options)
                )
            );
        },

        clearQueue: () => {
            notificationQueue.current = [];
        },

        getQueueStatus: () => ({
            pending: notificationQueue.current.length,
            isProcessing: isProcessing.current
        })
    }), [notification, isPushSupported, subscribeToPush, queueNotification, sendBackgroundNotification]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};